// src/components/Grid.tsx

import React, { FC, useEffect, useState } from 'react';
import { ContentfulClientApi } from 'contentful';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { BlogPosts } from './Data';

interface Props {
  data: BlogPosts[];
  client: ContentfulClientApi;
}

const Section = styled.section`
  text-align: center;
  margin: 2rem auto;
  max-width: 1440px;
  padding: 0 1rem;

  @media screen and (min-width: 768px) {
    padding: 0 2rem;
  }
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  align-items: stretch;    /* stretch every grid‐cell to the same height */
  margin-top: 2rem;

  @media screen and (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const CardLink = styled(Link)`
  display: flex;
  flex-direction: column;
  height: 100%;            /* fill the grid cell */
  text-decoration: none;
  color: inherit;
`;

const BlogCard = styled.div`
  flex: 1;                  /* take up all of CardLink’s height */
  display: flex;
  flex-direction: column;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  padding: 1.5rem;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const BlogImage = styled.img`
  width: 100%;
  height: 200px;           /* fixed height for uniformity */
  object-fit: cover;       /* crop/scale to fill */
  border-radius: 4px;
  margin-bottom: 1rem;

  @media screen and (max-width: 600px) {
    height: 150px;
  }
`;

const BlogTitle = styled.h2`
  font-size: 1.25rem;
  margin-bottom: 0.75rem;
  flex-shrink: 0;
`;

const Grid: FC<Props> = ({ data, client }) => {
  const [assets, setAssets] = useState<{ [id: string]: string }>({});

  useEffect(() => {
    data.forEach((post) => {
      client
        .getAsset(post.imageURL)
        .then((asset) =>
          setAssets((prev) => ({
            ...prev,
            [post.id]: asset.fields.file.url,
          }))
        )
        .catch((err) => console.error('Error fetching asset:', err));
    });
  }, [data, client]);

  const sortedData = [...data].sort((a, b) => +b.id - +a.id);

  return (
    <Section>
      <BlogGrid>
        {sortedData.map((post) => (
          <CardLink key={post.id} to={`/blog/${post.id}`}>
            <BlogCard>
              {assets[post.id] && (
                <BlogImage src={assets[post.id]} alt={post.title} />
              )}
              <BlogTitle>{post.title}</BlogTitle>
            </BlogCard>
          </CardLink>
        ))}
      </BlogGrid>
    </Section>
  );
};

export default Grid;
